// Two warnings that must be impossible to miss, on every page of the app.
//
// IMPERSONATION — starting one replaced the operator's own session with the
// customer's and said nothing anywhere. After a page reload there was no way to
// tell you were inside someone else's account, no way out except signing out and
// in again, and every action taken looked in the record like the customer's own
// work. The bar below is the fix that matters most: it is always visible, it
// names whose account this is, and Exit puts the operator straight back.
//
// MAINTENANCE — the toggle on Platform Ops 503s every non-superadmin on the
// platform. Left on by accident, the only person who can see the product is the
// one who turned it off, so nothing on their screen would ever remind them.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, LogOut, Wrench, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getMaintenance } from "@/api/admin";
import { toastError } from "@/lib/toast";
import { cn } from "@/lib/cn";

// Fired by Platform Ops after a successful save so the banner updates at once
// rather than on the next poll.
export const MAINTENANCE_CHANGED = "aiqa:maintenance-changed";

const BAR = "flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-sm font-medium";

function ImpersonationBar() {
  const { isImpersonating, impersonation, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);

  if (!isImpersonating) return null;

  const who = impersonation && !impersonation.unknown
    ? `${impersonation.email || impersonation.name || "this account"}${impersonation.company_name ? ` · ${impersonation.company_name}` : ""}`
    : "another account";

  const exit = () => {
    setLeaving(true);
    const restored = stopImpersonation();
    // Back to the platform surface when the operator's session came back;
    // to the sign-in page when it didn't (expired, or storage was cleared).
    navigate(restored ? "/admin/companies" : "/login", { replace: true });
    if (!restored) {
      toastError("Your operator session had expired — please sign in again.");
    }
    setLeaving(false);
  };

  return (
    <div className={cn(BAR, "bg-amber-500 text-amber-950")} role="status">
      <span className="inline-flex items-center gap-2">
        <AlertTriangle size={16} className="shrink-0" />
        <span>
          <strong>Viewing as {who}.</strong>{" "}
          Anything you do here is recorded against their account.
        </span>
      </span>
      <button
        type="button"
        onClick={exit}
        disabled={leaving}
        className="inline-flex items-center gap-1.5 rounded-lg bg-amber-950/90 px-3 py-1 text-xs font-semibold text-amber-50 transition-colors hover:bg-amber-950 disabled:opacity-60"
      >
        {leaving ? <Loader2 size={13} className="animate-spin" /> : <LogOut size={13} />}
        Return to admin
      </button>
    </div>
  );
}

function MaintenanceBar({ role }) {
  const [on, setOn] = useState(false);

  const check = useCallback(() => {
    // Only a platform operator can read this, and only they can act on it.
    if (role !== "superadmin") return;
    getMaintenance()
      .then((m) => setOn(!!(m && m.enabled)))
      // A failed read must not claim maintenance is off — but it also must not
      // claim it is on. Leave the last known state alone.
      .catch(() => {});
  }, [role]);

  useEffect(() => {
    check();
    const id = setInterval(check, 60000);
    window.addEventListener(MAINTENANCE_CHANGED, check);
    return () => {
      clearInterval(id);
      window.removeEventListener(MAINTENANCE_CHANGED, check);
    };
  }, [check]);

  if (role !== "superadmin" || !on) return null;

  return (
    <div className={cn(BAR, "bg-rose-600 text-white")} role="alert">
      <span className="inline-flex items-center gap-2">
        <Wrench size={16} className="shrink-0" />
        <span><strong>Maintenance mode is ON.</strong> Everyone except platform operators is getting a 503.</span>
      </span>
      <a
        href="/admin/platform-ops"
        className="rounded-lg bg-rose-950/40 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-rose-950/60"
      >
        Turn it off
      </a>
    </div>
  );
}

export default function SessionBanners({ role }) {
  return (
    <>
      <ImpersonationBar />
      <MaintenanceBar role={role} />
    </>
  );
}
